import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  LinearProgress,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Close as CloseIcon,
  EmojiEvents as TrophyIcon,
  Favorite as HeartIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { stickerTrackingService } from '../services/stickerTrackingService';


interface RoadmapModuleProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  planId: string;
  planTitle?: string;
  planDescription?: string;
  isArtifactView?: boolean; // New prop for artifact view mode
  onAskQuestion?: (context: import('../types/planQa').PlanQaContext) => void; // New prop for Plan Q&A
}

interface DayConfig {
  id: number;
  title: string;
  description: string;
  goals: string[];
  theme: string;
}

interface Progress {
  day: number;
  stickers: number;
  completedTasks: string[];
  lastCompleted?: Date;
}

// Multi-language day configurations
const getLocalizedDays = (planTitle: string, planDescription: string): DayConfig[] => {
  const isChinese = /[\u4e00-\u9fff]/.test(planTitle + planDescription);
  const isSpanish = /[áéíóúñü]/.test((planTitle + planDescription).toLowerCase());
  const isFrench = /[àâäéèêëïîôöùûüÿç]/.test((planTitle + planDescription).toLowerCase());

  const texts = {
    en: {
      toyDetective: 'Toy Detective',
      toyDetectiveDesc: 'Spot sharing opportunities and talk about feelings',
      spotSharing: 'Spot sharing',
      talkFeelings: 'Talk about feelings',

      turnTakingTimer: 'Turn-Taking Timer',
      turnTakingDesc: 'Practice taking turns with a timer',
      tapToPass: 'Tap to pass',
      followBell: 'Follow the bell',

      friendComesOver: 'Friend Comes Over',
      friendDesc: 'Invite a friend and practice sharing',
      sayYourTurn: 'Say "Your turn"',
      giveToy: 'Give a toy',

      stickerDay: 'Sticker Day',
      stickerDesc: 'Earn stickers and name the sharing rule',
      earnStickers: 'Earn stickers',
      nameRule: 'Name the rule',

      toyPartyI: 'Toy Party I',
      partyIDesc: 'Share multiple toys and practice waiting',
      share2Toys: 'Share 2 toys',
      wait5Sec: 'Wait 5 sec',

      toyPartyII: 'Toy Party II',
      partyIIDesc: 'Advanced sharing with gratitude',
      share3Toys: 'Share 3 toys',
      sayThankYou: 'Say thank you',

      sharingChampion: 'Sharing Champion',
      championDesc: 'Review all skills and celebrate success!',
      reviewAll: 'Review all',
      celebrate: 'Celebrate!'
    },
    'zh-CN': {
      toyDetective: '玩具侦探',
      toyDetectiveDesc: '发现分享机会并谈论感受',
      spotSharing: '发现分享',
      talkFeelings: '谈论感受',

      turnTakingTimer: '轮流计时器',
      turnTakingDesc: '用计时器练习轮流',
      tapToPass: '轻触传递',
      followBell: '跟随铃声',

      friendComesOver: '朋友来访',
      friendDesc: '邀请朋友并练习分享',
      sayYourTurn: '说"轮到你了"',
      giveToy: '给玩具',

      stickerDay: '贴纸日',
      stickerDesc: '获得贴纸并命名分享规则',
      earnStickers: '获得贴纸',
      nameRule: '命名规则',

      toyPartyI: '玩具派对一',
      partyIDesc: '分享多个玩具并练习等待',
      share2Toys: '分享2个玩具',
      wait5Sec: '等待5秒',

      toyPartyII: '玩具派对二',
      partyIIDesc: '高级分享与感恩',
      share3Toys: '分享3个玩具',
      sayThankYou: '说谢谢',

      sharingChampion: '分享冠军',
      championDesc: '回顾所有技能并庆祝成功！',
      reviewAll: '回顾所有',
      celebrate: '庆祝！'
    },
    'es': {
      toyDetective: 'Detective de Juguetes',
      toyDetectiveDesc: 'Identifica oportunidades de compartir y habla sobre sentimientos',
      spotSharing: 'Identifica compartir',
      talkFeelings: 'Habla sobre sentimientos',

      turnTakingTimer: 'Temporizador de Turnos',
      turnTakingDesc: 'Practica tomar turnos con un temporizador',
      tapToPass: 'Toca para pasar',
      followBell: 'Sigue la campana',

      friendComesOver: 'Viene un Amigo',
      friendDesc: 'Invita a un amigo y practica compartir',
      sayYourTurn: 'Di "Tu turno"',
      giveToy: 'Da un juguete',

      stickerDay: 'Día de Calcomanías',
      stickerDesc: 'Gana calcomanías y nombra la regla de compartir',
      earnStickers: 'Gana calcomanías',
      nameRule: 'Nombra la regla',

      toyPartyI: 'Fiesta de Juguetes I',
      partyIDesc: 'Comparte múltiples juguetes y practica esperar',
      share2Toys: 'Comparte 2 juguetes',
      wait5Sec: 'Espera 5 seg',

      toyPartyII: 'Fiesta de Juguetes II',
      partyIIDesc: 'Compartir avanzado con gratitud',
      share3Toys: 'Comparte 3 juguetes',
      sayThankYou: 'Di gracias',

      sharingChampion: 'Campeón de Compartir',
      championDesc: '¡Revisa todas las habilidades y celebra el éxito!',
      reviewAll: 'Revisa todo',
      celebrate: '¡Celebra!'
    },
    'fr': {
      toyDetective: 'Détective de Jouets',
      toyDetectiveDesc: 'Repérez les opportunités de partage et parlez des sentiments',
      spotSharing: 'Repérez le partage',
      talkFeelings: 'Parlez des sentiments',

      turnTakingTimer: 'Minuteur de Tours',
      turnTakingDesc: 'Pratiquez la prise de tours avec un minuteur',
      tapToPass: 'Appuyez pour passer',
      followBell: 'Suivez la cloche',

      friendComesOver: 'Un Ami Vient',
      friendDesc: 'Invitez un ami et pratiquez le partage',
      sayYourTurn: 'Dites "À toi"',
      giveToy: 'Donnez un jouet',

      stickerDay: 'Jour des Autocollants',
      stickerDesc: 'Gagnez des autocollants et nommez la règle de partage',
      earnStickers: 'Gagnez des autocollants',
      nameRule: 'Nommez la règle',

      toyPartyI: 'Fête des Jouets I',
      partyIDesc: 'Partagez plusieurs jouets et pratiquez l\'attente',
      share2Toys: 'Partagez 2 jouets',
      wait5Sec: 'Attendez 5 sec',

      toyPartyII: 'Fête des Jouets II',
      partyIIDesc: 'Partage avancé avec gratitude',
      share3Toys: 'Partagez 3 jouets',
      sayThankYou: 'Dites merci',

      sharingChampion: 'Champion du Partage',
      championDesc: 'Révisez toutes les compétences et célébrez le succès !',
      reviewAll: 'Révisez tout',
      celebrate: 'Célébrez !'
    }
  };

  const language = isChinese ? 'zh-CN' : isSpanish ? 'es' : isFrench ? 'fr' : 'en';
  const t = texts[language];

  return [
    {
      id: 1,
      title: t.toyDetective,
      description: t.toyDetectiveDesc,
      goals: [t.spotSharing, t.talkFeelings],
      theme: 'indigo'
    },
    {
      id: 2,
      title: t.turnTakingTimer,
      description: t.turnTakingDesc,
      goals: [t.tapToPass, t.followBell],
      theme: 'purple'
    },
    {
      id: 3,
      title: t.friendComesOver,
      description: t.friendDesc,
      goals: [t.sayYourTurn, t.giveToy],
      theme: 'emerald'
    },
    {
      id: 4,
      title: t.stickerDay,
      description: t.stickerDesc,
      goals: [t.earnStickers, t.nameRule],
      theme: 'amber'
    },
    {
      id: 5,
      title: t.toyPartyI,
      description: t.partyIDesc,
      goals: [t.share2Toys, t.wait5Sec],
      theme: 'sky'
    },
    {
      id: 6,
      title: t.toyPartyII,
      description: t.partyIIDesc,
      goals: [t.share3Toys, t.sayThankYou],
      theme: 'rose'
    },
    {
      id: 7,
      title: t.sharingChampion,
      description: t.championDesc,
      goals: [t.reviewAll, t.celebrate],
      theme: 'lime'
    }
  ];
};

// DAYS will be initialized in the component to use actual props

const RoadmapModule: React.FC<RoadmapModuleProps> = ({
  open,
  onClose,
  userId,
  planId,
  planTitle = '🌟 Share Quest',
  planDescription = 'Week plan: Help your child practice sharing toys!',
  isArtifactView = false
}) => {
  const [progress, setProgress] = useState<Progress>({ day: 1, stickers: 0, completedTasks: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Initialize localized days based on plan props
  const DAYS = getLocalizedDays(planTitle, planDescription);
  const totalDays = DAYS.length;

  // Multi-language text mappings
  const getText = (key: string) => {
    // Detect language from planTitle and planDescription
    const isChinese = /[\u4e00-\u9fff]/.test(planTitle + planDescription);
    const isSpanish = /[áéíóúñü]/.test((planTitle + planDescription).toLowerCase());
    const isFrench = /[àâäéèêëïîôöùûüÿç]/.test((planTitle + planDescription).toLowerCase());

    if (isChinese) {
      const chineseTexts: Record<string, string> = {
        progress: '进度',
        days: '天',
        yourJourney: '您的旅程',
        todayTask: '今日任务',
        goalsForToday: '今日目标',
        stickersEarned: '获得贴纸',
        daysCompleted: '天已完成',
        close: '关闭',
        markAsDone: '标记完成',
        saving: '保存中...'
      };
      return chineseTexts[key] || key;
    } else if (isSpanish) {
      const spanishTexts: Record<string, string> = {
        progress: 'Progreso',
        days: 'días',
        yourJourney: 'Tu Viaje',
        todayTask: 'Tarea de Hoy',
        goalsForToday: 'Objetivos para hoy',
        stickersEarned: 'Calcomanías ganadas',
        daysCompleted: 'días completados',
        close: 'Cerrar',
        markAsDone: 'Marcar como hecho',
        saving: 'Guardando...'
      };
      return spanishTexts[key] || key;
    } else if (isFrench) {
      const frenchTexts: Record<string, string> = {
        progress: 'Progrès',
        days: 'jours',
        yourJourney: 'Votre Voyage',
        todayTask: 'Tâche d\'Aujourd\'hui',
        goalsForToday: 'Objectifs pour aujourd\'hui',
        stickersEarned: 'Autocollants gagnés',
        daysCompleted: 'jours terminés',
        close: 'Fermer',
        markAsDone: 'Marquer comme terminé',
        saving: 'Enregistrement...'
      };
      return frenchTexts[key] || key;
    } else {
      // English
      const englishTexts: Record<string, string> = {
        progress: 'Progress',
        days: 'days',
        yourJourney: 'Your Journey',
        todayTask: 'Today\'s Task',
        goalsForToday: 'Goals for today',
        stickersEarned: 'Stickers earned',
        daysCompleted: 'days completed',
        close: 'Close',
        markAsDone: 'Mark as Done',
        saving: 'Saving...'
      };
      return englishTexts[key] || key;
    }
  };

  // Load progress from Firestore
  useEffect(() => {
    const fetchProgress = async () => {
      if (!userId || !planId) return;

      try {
        const ref = doc(db, 'users', userId, 'plans', planId);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() as Progress;
          setProgress(data);
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
      }
    };

    if (open) {
      fetchProgress();
    }
  }, [userId, planId, open]);

  // Save progress to Firestore
  const saveProgress = async (newProgress: Progress) => {
    if (!userId || !planId) return;

    try {
      setIsLoading(true);
      const ref = doc(db, 'users', userId, 'plans', planId);
      await setDoc(ref, newProgress, { merge: true });
      setProgress(newProgress);
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle completing a day
  const handleCompleteDay = async () => {
    const nextDay = progress.day + 1;
    const isLastDay = nextDay > totalDays;

    // Detect language from planTitle and planDescription
    const isChinese = /[\u4e00-\u9fff]/.test(planTitle + planDescription);
    const isSpanish = /[áéíóúñü]/.test((planTitle + planDescription).toLowerCase());
    const isFrench = /[àâäéèêëïîôöùûüÿç]/.test((planTitle + planDescription).toLowerCase());
    const userLanguage = isChinese ? 'zh-CN' : isSpanish ? 'es' : isFrench ? 'fr' : 'en';

    try {
      // Award stickers using the new tracking system
      const stickerResult = await stickerTrackingService.awardDayCompletionStickers(
        userId,
        planId,
        planTitle,
        'template', // Plan type for template roadmaps
        progress.day,
        totalDays,
        isLastDay,
        userLanguage
      );

      console.log('🏆 Stickers awarded:', stickerResult);

      if (nextDay <= totalDays) {
        const newProgress: Progress = {
          day: nextDay,
          stickers: progress.stickers + stickerResult.stickersEarned,
          completedTasks: [...progress.completedTasks, `day-${progress.day}`],
          lastCompleted: new Date()
        };

        await saveProgress(newProgress);

        // Show confetti animation
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2000);
      }
    } catch (error) {
      console.error('❌ Error in handleCompleteDay:', error);
      // Still show confetti for user feedback even if sticker tracking fails
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2000);
    }
  };

  // Calculate progress percentage
  const progressPercentage = Math.min(100, Math.round((progress.completedTasks.length / totalDays) * 100));

  // Get current day config
  const currentDay = DAYS.find(d => d.id === progress.day) || DAYS[0];

  // If it's artifact view, render inline content instead of dialog
  if (isArtifactView) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header for artifact view */}
        <Box sx={{
          p: 3,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          background: 'linear-gradient(135deg, #F4A261 0%, #E76F51 100%)',
          color: 'white'
        }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 800,
                fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                letterSpacing: '-0.02em'
              }}
            >
              {planTitle}
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.2)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography
            variant="h6"
            sx={{
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              lineHeight: 1.6,
              opacity: 0.95,
              fontWeight: 400,
              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
            }}
          >
            {planDescription}
          </Typography>
        </Box>

        {/* Content for artifact view */}
        <Box sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 3
        }}>
          {/* Progress Bar */}
          <Box sx={{ mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" color="text.secondary">
                {getText('progress')}: {progress.completedTasks.length}/{totalDays} {getText('daysCompleted')}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {progressPercentage}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{ height: 12, borderRadius: 6 }}
            />
          </Box>

          {/* Roadmap Days Grid */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              {getText('yourJourney')}
            </Typography>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 2
            }}>
              {DAYS.map((day) => {
                const isCompleted = day.id < progress.day;
                const isCurrent = day.id === progress.day;
                const isLocked = day.id > progress.day;

                return (
                  <Card
                    key={day.id}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      cursor: isLocked ? 'default' : 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      transform: isCurrent ? 'scale(1.05)' : 'scale(1)',
                      ...(isCompleted && {
                        bgcolor: 'success.light',
                        color: 'success.contrastText'
                      }),
                      ...(isCurrent && {
                        bgcolor: 'warning.light',
                        color: 'warning.contrastText',
                        boxShadow: 3
                      }),
                      ...(isLocked && {
                        bgcolor: 'grey.100',
                        color: 'text.disabled'
                      })
                    }}
                  >
                    <Box display="flex" flexDirection="column" alignItems="center">
                      {isCompleted ? (
                        <CheckCircleIcon sx={{ fontSize: 32, mb: 1 }} />
                      ) : isCurrent ? (
                        <StarIcon sx={{ fontSize: 32, mb: 1 }} />
                      ) : (
                        <StarIcon sx={{ fontSize: 32, mb: 1, opacity: 0.3 }} />
                      )}
                      <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                        {getText('day')} {day.id}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {day.title}
                      </Typography>
                    </Box>
                  </Card>
                );
              })}
            </Box>
          </Box>

          {/* Current Task */}
          <Card sx={{ mb: 4, bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <TimerIcon sx={{ mr: 2, color: 'warning.main' }} />
                {getText('todayTask')} (Day {progress.day})
              </Typography>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>
                {currentDay.title}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {currentDay.description}
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>
                  {getText('goalsForToday')}:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {currentDay.goals.map((goal, index) => (
                    <Chip
                      key={index}
                      label={goal}
                      size="medium"
                      sx={{
                        fontSize: '1rem',
                        py: 1
                      }}
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>

              {/* Mark as Done button for current day */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 3,
                borderTop: 1,
                borderColor: 'divider'
              }}>
                <Button
                  onClick={handleCompleteDay}
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                  sx={{
                    py: 2.5,
                    px: 5,
                    borderRadius: 6,
                    minWidth: 180,
                    background: 'linear-gradient(135deg, #48B0A5 0%, #2A9D8F 100%)',
                    boxShadow: '0 10px 30px rgba(0, 200, 83, 0.4), 0 4px 15px rgba(0, 200, 83, 0.2)',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                    textTransform: 'none',
                    letterSpacing: '0.5px',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                      transition: 'left 0.5s',
                    },
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2A9D8F 0%, #48B0A5 50%, #87CEEB 100%)',
                      transform: 'translateY(-3px) scale(1.02)',
                      boxShadow: '0 15px 40px rgba(42, 157, 143, 0.6), 0 8px 25px rgba(42, 157, 143, 0.3)',
                      '&::before': {
                        left: '100%',
                      }
                    },
                    '&:active': {
                      transform: 'translateY(-1px) scale(1.01)',
                      boxShadow: '0 8px 25px rgba(0, 200, 83, 0.4)',
                      transition: 'all 0.1s'
                    }
                  }}
                >
                  {isLoading ? getText('saving') : getText('markAsDone')}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Stats */}
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center">
              <HeartIcon sx={{ mr: 2, color: 'error.main', fontSize: 28 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                }}
              >
                {getText('stickersEarned')}: {progress.stickers}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center">
              <TrophyIcon sx={{ mr: 2, color: 'warning.main', fontSize: 28 }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                }}
              >
                {progress.completedTasks.length}/{totalDays} {getText('daysCompleted')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Simple Confetti Animation */}
        {showConfetti && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Typography
              variant="h1"
              sx={{
                color: 'success.main',
                animation: 'bounce 0.6s ease-in-out',
                '@keyframes bounce': {
                  '0%, 20%, 50%, 80%, 100%': {
                    transform: 'translateY(0)',
                  },
                  '40%': {
                    transform: 'translateY(-30px)',
                  },
                  '60%': {
                    transform: 'translateY(-15px)',
                  },
                },
              }}
            >
              🎉
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Original dialog view for backward compatibility
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100vh' : '90vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
            {planTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            lineHeight: 1.5
          }}
        >
          {planDescription}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Progress Bar */}
        <Box sx={{ mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" color="text.secondary">
              {getText('progress')}: {progress.completedTasks.length}/{totalDays} {getText('daysCompleted')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {progressPercentage}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Roadmap Days Grid */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {getText('yourJourney')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1 }}>
            {DAYS.map((day) => {
              const isCompleted = day.id < progress.day;
              const isCurrent = day.id === progress.day;
              const isLocked = day.id > progress.day;

              return (
                <Card
                  key={day.id}
                  sx={{
                    p: 1,
                    textAlign: 'center',
                    cursor: isLocked ? 'default' : 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    transform: isCurrent ? 'scale(1.05)' : 'scale(1)',
                    ...(isCompleted && {
                      bgcolor: 'success.light',
                      color: 'success.contrastText'
                    }),
                    ...(isCurrent && {
                      bgcolor: 'warning.light',
                      color: 'warning.contrastText',
                      boxShadow: 2
                    }),
                    ...(isLocked && {
                      bgcolor: 'grey.100',
                      color: 'text.disabled'
                    })
                  }}
                >
                  <Box display="flex" flexDirection="column" alignItems="center">
                    {isCompleted ? (
                      <CheckCircleIcon sx={{ fontSize: 24, mb: 0.5 }} />
                    ) : isCurrent ? (
                      <StarIcon sx={{ fontSize: 24, mb: 0.5 }} />
                    ) : (
                      <StarIcon sx={{ fontSize: 24, mb: 0.5, opacity: 0.3 }} />
                    )}
                    <Typography variant="caption" sx={{ fontWeight: 'medium' }}>
                      {getText('day')} {day.id}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                      {day.title}
                    </Typography>
                  </Box>
                </Card>
              );
            })}
          </Box>
        </Box>

        {/* Current Task */}
        <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              <TimerIcon sx={{ mr: 1, color: 'warning.main' }} />
              {getText('todayTask')} (Day {progress.day})
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 'medium' }}>
              {currentDay.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {currentDay.description}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
                {getText('goalsForToday')}:
              </Typography>
              {currentDay.goals.map((goal, index) => (
                <Chip
                  key={index}
                  label={goal}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </CardContent>
        </Card>

        {/* Stats */}
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center">
            <HeartIcon sx={{ mr: 1, color: 'error.main' }} />
            <Typography variant="body2">
              {getText('stickersEarned')}: {progress.stickers}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <TrophyIcon sx={{ mr: 1, color: 'warning.main' }} />
            <Typography variant="body2">
              {progress.completedTasks.length}/{totalDays} {getText('daysCompleted')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined">
          {getText('close')}
        </Button>
        {progress.day <= totalDays && (
          <Button
            onClick={handleCompleteDay}
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {isLoading ? getText('saving') : getText('markAsDone')}
          </Button>
        )}
      </DialogActions>

      {/* Simple Confetti Animation */}
      {showConfetti && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: 'success.main',
              animation: 'bounce 0.6s ease-in-out',
              '@keyframes bounce': {
                '0%, 20%, 50%, 80%, 100%': {
                  transform: 'translateY(0)',
                },
                '40%': {
                  transform: 'translateY(-30px)',
                },
                '60%': {
                  transform: 'translateY(-15px)',
                },
              },
            }}
          >
            🎉
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default RoadmapModule;
